import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEOHead';
import { FileText, Shield, Scale, AlertCircle, Mail } from 'lucide-react';

const TermsOfServicePage = () => {
  const seoConfig = {
    title: 'Termini di Servizio | WEQASA',
    description: 'Termini e condizioni di utilizzo della piattaforma WEQASA per la compravendita immobiliare in Italia.',
    keywords: 'termini di servizio weqasa, condizioni uso piattaforma immobiliare'
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...seoConfig} />
      <Header />

      {/* Hero Section */}
      <section className="bg-[#F5F5F7] py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#0071e3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-[#0071e3]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
            Termini di Servizio
          </h1>
          <p className="text-lg text-[#86868B]">
            Ultimo aggiornamento: Dicembre 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduzione */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">1. Introduzione</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Benvenuto su WEQASA. I presenti Termini di Servizio ("Termini") regolano l'accesso e l'utilizzo 
                della piattaforma WEQASA, accessibile tramite il sito web weqasa.it e le relative applicazioni 
                (collettivamente, il "Servizio").
              </p>
              <p className="text-[#424245] leading-relaxed">
                Utilizzando il Servizio, l'utente accetta di essere vincolato dai presenti Termini. 
                Se non si accettano i Termini, si prega di non utilizzare il Servizio.
              </p>
            </div>

            {/* Definizioni */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">2. Definizioni</h2>
              <ul className="space-y-3 text-[#424245]">
                <li><strong>"WEQASA"</strong> o <strong>"Piattaforma"</strong>: il servizio di intermediazione immobiliare online gestito da WEQASA S.r.l.</li>
                <li><strong>"Utente"</strong>: qualsiasi persona fisica o giuridica che accede o utilizza il Servizio.</li>
                <li><strong>"Proprietario"</strong> o <strong>"Venditore"</strong>: l'utente che intende vendere un immobile tramite la Piattaforma.</li>
                <li><strong>"Acquirente"</strong>: l'utente che intende acquistare un immobile tramite la Piattaforma.</li>
                <li><strong>"Agente"</strong>: il professionista immobiliare verificato che opera sulla Piattaforma.</li>
                <li><strong>"Gara di Vendita"</strong>: il sistema di vendita competitiva al rialzo utilizzato dalla Piattaforma.</li>
              </ul>
            </div>

            {/* Registrazione */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">3. Registrazione e Account</h2>
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">3.1 Requisiti</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                Per utilizzare alcune funzionalità del Servizio, è necessario creare un account. L'utente deve:
              </p>
              <ul className="space-y-2 text-[#424245] mb-6">
                <li>• Avere almeno 18 anni di età</li>
                <li>• Fornire informazioni accurate, complete e aggiornate</li>
                <li>• Mantenere la riservatezza delle proprie credenziali di accesso</li>
                <li>• Notificare immediatamente WEQASA in caso di uso non autorizzato dell'account</li>
              </ul>
              
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">3.2 Verifica dell'Identità</h3>
              <p className="text-[#424245] leading-relaxed">
                WEQASA si riserva il diritto di richiedere documenti di identità e altra documentazione 
                per verificare l'identità degli utenti, in conformità con le normative antiriciclaggio vigenti.
              </p>
            </div>

            {/* Servizi */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">4. Descrizione dei Servizi</h2>
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">4.1 Per i Venditori</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                I Proprietari possono inserire i propri immobili sulla Piattaforma per la vendita tramite 
                il sistema di Gara di Vendita. Il servizio include:
              </p>
              <ul className="space-y-2 text-[#424245] mb-6">
                <li>• Valutazione preliminare dell'immobile</li>
                <li>• Creazione dell'annuncio con foto e documentazione</li>
                <li>• Gestione della Gara di Vendita</li>
                <li>• Supporto nella fase di trattativa e rogito</li>
              </ul>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">4.2 Per gli Acquirenti</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                Gli Acquirenti possono cercare immobili, partecipare alle Gare di Vendita e acquistare 
                al miglior prezzo. Il servizio include:
              </p>
              <ul className="space-y-2 text-[#424245] mb-6">
                <li>• Accesso al catalogo immobili verificati</li>
                <li>• Prenotazione visite</li>
                <li>• Partecipazione alle Gare di Vendita</li>
                <li>• Assistenza nel processo di acquisto</li>
              </ul>
            </div>

            {/* Gara di Vendita */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">5. Regolamento Gara di Vendita</h2>
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">5.1 Partecipazione</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                Per partecipare a una Gara di Vendita, l'Acquirente deve:
              </p>
              <ul className="space-y-2 text-[#424245] mb-6">
                <li>• Essere registrato e verificato sulla Piattaforma</li>
                <li>• Aver visitato l'immobile (virtualmente o di persona)</li>
                <li>• Accettare il regolamento specifico della Gara</li>
                <li>• Versare eventuale deposito cauzionale richiesto</li>
              </ul>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">5.2 Offerte</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                Le offerte presentate durante la Gara di Vendita sono vincolanti. L'Acquirente che presenta 
                l'offerta più alta al termine della Gara si impegna ad acquistare l'immobile alle condizioni 
                stabilite.
              </p>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">5.3 Conclusione</h3>
              <p className="text-[#424245] leading-relaxed">
                Al termine della Gara, il Venditore ha facoltà di accettare o rifiutare l'offerta più alta, 
                salvo diversamente specificato nel regolamento della singola Gara.
              </p>
            </div>

            {/* Commissioni */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">6. Commissioni e Pagamenti</h2>
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">6.1 Commissioni di Servizio</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                WEQASA applica commissioni per i servizi resi, come dettagliato nella sezione "Tariffe" 
                del sito. Le commissioni possono variare in base al tipo di servizio e al valore dell'immobile.
              </p>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">6.2 Modalità di Pagamento</h3>
              <p className="text-[#424245] leading-relaxed">
                I pagamenti vengono elaborati tramite sistemi sicuri e certificati. WEQASA utilizza 
                Stripe per la gestione dei pagamenti elettronici, garantendo la massima sicurezza 
                delle transazioni.
              </p>
            </div>

            {/* Responsabilità */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">7. Limitazione di Responsabilità</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                WEQASA agisce come intermediario tecnologico e non è parte nelle transazioni immobiliari 
                tra Venditori e Acquirenti. Pertanto:
              </p>
              <ul className="space-y-2 text-[#424245]">
                <li>• WEQASA non garantisce la conclusione delle transazioni</li>
                <li>• WEQASA non è responsabile per vizi occulti degli immobili</li>
                <li>• WEQASA non è responsabile per dichiarazioni false degli utenti</li>
                <li>• La responsabilità di WEQASA è limitata all'importo delle commissioni pagate</li>
              </ul>
            </div>

            {/* Proprietà Intellettuale */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">8. Proprietà Intellettuale</h2>
              <p className="text-[#424245] leading-relaxed">
                Tutti i contenuti presenti sulla Piattaforma, inclusi ma non limitati a testi, grafiche, 
                loghi, icone, immagini, software e codice, sono di proprietà di WEQASA S.r.l. o dei 
                rispettivi titolari e sono protetti dalle leggi sul diritto d'autore e sulla proprietà 
                intellettuale. È vietata qualsiasi riproduzione non autorizzata.
              </p>
            </div>

            {/* Modifiche */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">9. Modifiche ai Termini</h2>
              <p className="text-[#424245] leading-relaxed">
                WEQASA si riserva il diritto di modificare i presenti Termini in qualsiasi momento. 
                Le modifiche saranno comunicate agli utenti tramite email e/o avviso sulla Piattaforma. 
                L'uso continuato del Servizio dopo la pubblicazione delle modifiche costituisce 
                accettazione dei nuovi Termini.
              </p>
            </div>

            {/* Legge Applicabile */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">10. Legge Applicabile e Foro Competente</h2>
              <p className="text-[#424245] leading-relaxed">
                I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia 
                derivante dall'interpretazione o esecuzione dei presenti Termini, sarà competente 
                in via esclusiva il Foro di Roma, salvo quanto previsto dalle norme inderogabili 
                a tutela dei consumatori.
              </p>
            </div>

            {/* Contatti */}
            <div className="bg-[#F5F5F7] rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">11. Contatti</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Per qualsiasi domanda relativa ai presenti Termini di Servizio, è possibile contattarci:
              </p>
              <div className="space-y-2 text-[#424245]">
                <p><strong>WEQASA S.r.l.</strong></p>
                <p>Via Roma 123, 00100 Roma (RM)</p>
                <p>Email: <a href="mailto:legal@weqasa.it" className="text-[#0071e3] hover:underline">legal@weqasa.it</a></p>
                <p>PEC: weqasa@pec.it</p>
                <p>P.IVA: 12345678901</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
